// Sprint 9.5 — FunnelChart
// SVG inline funnel chart (4 step: invited → onboarded → first_plan → active_30d).
//
// Layout: trapezi orizzontali decrescenti che mostrano % conversione tra step.
// Highlight: step con max drop-off ha border accent.
// Click step → drill-down lista pazienti bloccati.

import { useState } from 'react';
import { ConversionFunnel, FunnelStep } from '../lib/api';

interface FunnelChartProps {
  funnel: ConversionFunnel;
  onStepClick?: (step: FunnelStep) => void;
}

const STEP_LABELS: Record<FunnelStep, string> = {
  invited: 'Invitati',
  onboarded: 'Onboarded',
  first_plan: 'Primo piano',
  active_30d: 'Attivi 30gg',
};

export function FunnelChart({ funnel, onStepClick }: FunnelChartProps) {
  const [hover, setHover] = useState<FunnelStep | null>(null);

  const steps: Array<{ key: FunnelStep; count: number; conv: number | null }> = [
    { key: 'invited', count: funnel.invited, conv: null },
    { key: 'onboarded', count: funnel.onboarded, conv: funnel.conv_invited_onboarded_pct },
    { key: 'first_plan', count: funnel.first_plan, conv: funnel.conv_onboarded_first_plan_pct },
    { key: 'active_30d', count: funnel.active_30d, conv: funnel.conv_first_plan_active_pct },
  ];

  const W = 460;
  const STEP_H = 56;
  const STEP_GAP = 6;
  const H = steps.length * (STEP_H + STEP_GAP);
  const maxCount = Math.max(1, funnel.invited);

  if (funnel.invited === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--mute, #888)', fontSize: 13 }}>
        Nessun paziente nel funnel. Inizia con il primo invito.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', maxWidth: W }}
        role="img"
        aria-label="Funnel conversione"
      >
        {steps.map((s, i) => {
          const y = i * (STEP_H + STEP_GAP);
          const ratio = s.count / maxCount;
          const stepW = Math.max(80, ratio * W);
          const x = (W - stepW) / 2;
          const isMaxDrop = funnel.max_drop_off_step === s.key && s.key !== 'active_30d';
          const isHovered = hover === s.key;

          const color = i === 0
            ? '#4a90e2'
            : i === 1
              ? '#0b8a3e'
              : i === 2
                ? '#e67e22'
                : '#9b59b6';

          return (
            <g
              key={s.key}
              onMouseEnter={() => setHover(s.key)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onStepClick?.(s.key)}
              style={{ cursor: onStepClick ? 'pointer' : 'default' }}
              role={onStepClick ? 'button' : undefined}
              aria-label={`${STEP_LABELS[s.key]}: ${s.count}${s.conv !== null ? ` (${s.conv}% da step precedente)` : ''}`}
            >
              <rect
                x={x}
                y={y}
                width={stepW}
                height={STEP_H}
                rx={6}
                ry={6}
                fill={color}
                opacity={isHovered ? 1 : 0.85}
                stroke={isMaxDrop ? '#c0392b' : isHovered ? 'var(--ink, #111)' : 'none'}
                strokeWidth={2.5}
                strokeDasharray={isMaxDrop ? '4,3' : ''}
              />
              <text
                x={W / 2}
                y={y + STEP_H / 2 - 4}
                fontSize={14}
                fontWeight={700}
                fill="#fff"
                textAnchor="middle"
              >
                {STEP_LABELS[s.key]}
              </text>
              <text
                x={W / 2}
                y={y + STEP_H / 2 + 14}
                fontSize={12}
                fontFamily="var(--mono, monospace)"
                fontWeight={600}
                fill="#fff"
                textAnchor="middle"
              >
                {s.count}{s.conv !== null ? ` · ${s.conv}%` : ''}
              </text>
            </g>
          );
        })}
      </svg>
      {funnel.max_drop_off_step && (
        <div
          style={{
            marginTop: 8,
            padding: '6px 10px',
            background: 'rgba(192,57,43,0.08)',
            border: '1px solid rgba(192,57,43,0.2)',
            borderRadius: 6,
            fontSize: 11.5,
            color: '#c0392b',
            fontWeight: 500,
          }}
        >
          Max drop-off allo step <b>{STEP_LABELS[funnel.max_drop_off_step]}</b>. Click sul box per il dettaglio.
        </div>
      )}
    </div>
  );
}
